import styled, { ThemedStyledProps } from 'styled-components';
import { compose, fontSize, Theme, variant } from 'styled-system';

import {
  BORDER,
  COMMON,
  get,
  LAYOUT,
  SystemCommonProps,
  SystemLayoutProps
} from '../theme/constants';
import { pxToRem } from '../theme/utils/pxToRem';
import sx, { SxProp } from '../theme/utils/sx';

const composeSystem = compose(COMMON, LAYOUT, BORDER, fontSize);

export interface StyledButtonProps
  extends SxProp,
    SystemCommonProps,
    Omit<SystemLayoutProps, 'size'> {
  colorSchema?: 'active' | 'success' | 'warning' | 'error';
  sizeVariant?: 'icon' | 'small' | 'standard' | 'large';
  variant?: 'outlined' | 'filled' | 'unfilled';
}

const variants = (props: ThemedStyledProps<StyledButtonProps, Theme>) => {
  const schema = props.colorSchema;
  const color = props.theme.colors[schema];
  return variant({
    variants: {
      outlined: {
        color: color.base,
        bg: 'transparent',
        borderWidth: '1px',
        borderColor: color.base,
        borderStyle: 'solid',
        '&:hover, &:focus': {
          color: color.hover,
          borderColor: color.hover
        },
        '&:disabled': {
          bg: 'transparent',
          color: get('colors.grey.50'),
          borderColor: get('colors.grey.50'),
          cursor: 'not-allowed'
        }
      },
      filled: {
        color: 'white',
        bg: color.base,
        '&:hover, &:focus': {
          bg: color.hover
        },
        '&:disabled': {
          bg: get('colors.grey.10'),
          color: get('colors.grey.50'),
          cursor: 'not-allowed'
        }
      },
      unfilled: {
        bg: 'transparent',
        color: color.base,
        '&:hover, &:focus': {
          bg: get('colors.grey.10'),
          color: color.hover
        },
        '&:disabled': {
          color: get('colors.grey.50'),
          bg: 'transparent',
          cursor: 'not-allowed'
        }
      }
    }
  });
};

const sizeVariant = variant({
  prop: 'sizeVariant',
  variants: {
    icon: {
      p: 1,
      fontSize: '1.5rem'
    },
    small: {
      minWidth: pxToRem(64),
      height: pxToRem(32),
      px: pxToRem(16),
      fontSize: pxToRem(14)
    },
    standard: {
      minWidth: pxToRem(96),
      px: pxToRem(16),
      height: pxToRem(48),
      fontSize: pxToRem(16)
    },
    large: {
      width: pxToRem(72),
      height: pxToRem(72),
      borderRadius: pxToRem(9999),
      fontSize: pxToRem(16)
    }
  }
});

const StyledButton = styled.button<StyledButtonProps>`
  transition: all 0.3s ease-in-out;
  display: flex;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  appearance: none;
  outline: none;
  cursor: pointer;
  border-radius: 30px;
  border: none;
  transition: background-color 0.5s ease;
  text-decoration: none;
  box-sizing: border-box;
  ${composeSystem}
  ${variants}
  ${sizeVariant}
  ${sx}
`;

StyledButton.defaultProps = {
  colorSchema: 'active',
  sizeVariant: 'standard',
  variant: 'filled'
};

export default StyledButton;
